<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
      <v-row class="ma-0 mb-3">
        <h2>{{title}}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter"
          >Apply Filters</v-btn
        >&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="load"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert
        width="100%"
        class="mr-5 ml-5"
        v-model="isError"
        dismissible
        type="error"
      >
        {{ errorMessage }}</v-alert
      >
    </v-row>
    <v-data-table
      dense
      disable-sort
      hide-default-footer
      class="elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
    >
      <template v-slot:header.litterId="{ header }">
        <v-text-field
          v-model="searchParams.litterId"
          label="Litter ID"
          clearable
          @click:clear="searchParams.litterId = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.dogId="{ header }">
        <v-text-field
          v-model="searchParams.dogId"
          label="Dog ID"
          clearable
          @click:clear="searchParams.dogId = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.releasedPerson="{ header }">
          <div>
              <v-text-field v-model="searchParams.releasePersonEmail"
                label="Email"
                clearable
                @click:clear="searchParams.releasePersonEmail = ''"
                v-on:keyup.enter="applyFilter"
              />
              <v-text-field hide-details
                class="mt-0 pt-0 mb-2"
                v-model="searchParams.releasePersonName"
                label="Name"
                clearable
                @click:clear="searchParams.releasePersonEmail = ''"
                v-on:keyup.enter="applyFilter"
              />
          </div>
      </template>
      <template v-slot:header.releaseDate>
        <div width="300px">
          <datepicker
            :date="searchParams.releaseDateFrom"
            label="Release Date From"
            v-on:updateDates="updateDates($event, 'releaseDateFrom')"
            v-on:enter="updateDateSearch($event, 'releaseDateFrom')"
          />
          <datepicker
            :date="searchParams.releaseDateTo"
            label="Release Date To"
            v-on:updateDates="updateDates($event, 'releaseDateTo')"
            v-on:enter="updateDateSearch($event, 'releaseDateTo')"
          />
        </div>
      </template>
      <template v-slot:header.registrationStatus="{ header }">
        <div>
          <v-checkbox
            :indeterminate="searchParams.registrationStatus == null"
            v-model="searchParams.registrationStatus"
            label="Registration Status"
            v-on:change="toggleCheckbox"
          />
        </div>
      </template>

      <template v-slot:item.actionButton="{ item }">
        <v-btn
          color="red"
          class="white--text my-1"
          @click="showEmailModal(item.releasePersonEmail, item.releasePersonFirstName, item.releasePersonLastName, item.puppyReleaseId)"
          >Resend Email</v-btn
        ><br />
        <v-btn
          color="primary"
          class="white--text my-1"
          disabled
          @click="alert('To be implemented')"
          >Cancel</v-btn
        >
      </template>
      <template v-slot:item.litterId="{ item }">
        <router-link :to="`/admin/litters/${item.litterId}`" target="_blank">{{
          item.litterId
        }}</router-link>
      </template>
      <template v-slot:item.releasedPerson="{ item }">
         {{ `Name: ${item.releasePersonFirstName} ${item.releasePersonLastName}` }} <br /> Email: <a :href="`mailto:${item.releasePersonEmail}`">{{ item.releasePersonEmail }}</a>
      </template>
      <template v-slot:item.dogId="{ item }">
        <router-link :to="`/dogs/${encodeURIComponent(item.dogId)}`" target="_blank">{{
          item.dogId
        }}</router-link>
      </template>  
      <template v-slot:item.puppyInfo="{ item }">        
         {{ item.puppyBody }} <br /> {{ item.puppyGender }}
      </template>
      <template v-slot:item.signatureStatus="{ item }">
         <signature-status-list hideHeader :signatories="item.signatories" />
      </template>
      <template v-slot:item.registrationStatus="{ item }">
        <span>
          <v-chip
            v-if="item.registrationStatus != null"
            class="my-2"
            :color="item.registrationStatus ? 'green' : 'red'"
            text-color="white"
          >
            <v-avatar left>
              <v-icon v-if="item.registrationStatus"
                >mdi-checkbox-marked-circle</v-icon
              >
              <v-icon v-else>mdi-close-circle-outline</v-icon>
            </v-avatar>
            {{
              item.registrationStatus
                ? "Registered"
                : "Not Registered"
            }}
          </v-chip>
        </span>
      </template>
      <template v-slot:item.releaseDate="{ item }">
        <span>{{
          item.releaseDate
            ? $dateUtils.formatDate(new Date(item.releaseDate))
            : ""
        }}</span>
      </template>
    </v-data-table>
    <v-dialog v-model="isEmailModalVisible" max-width="500px">
        <v-card>
            <v-card-title style="word-break: break-word" class="text-h5">
              Update Release Info and Resend Email
            </v-card-title>
            <v-card-text>
              <v-text-field
              v-model="emailToSend"
              label="Email"
              />
              <v-text-field
              v-model="firstNameToSend"
              label="First Name"
              />
              <v-text-field
              v-model="lastNameToSend"
              label="Last Name"
              />
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="isEmailModalVisible = false">
                    Cancel
                </v-btn>
                <v-btn color="blue darken-1" text @click="sendEmail">
                    OK
                </v-btn>
                <v-spacer />
            </v-card-actions>
        </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";
import SignatureStatusList from "@/components/common/SignatureStatusList.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";
import dogService from "@/services/admin/dogService";
import * as Sentry from "@sentry/vue";

import { UserInfo } from "@/types/User";
import { MetaNames } from "@/constants";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

export default Vue.extend({
  name: "PuppyReleaseList",
  components: {
    Datepicker,
    SignatureStatusList,
    PaginationSelect
  },
  data: function () {
    return {
      title: MetaNames.AdminPuppyReleases,
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      isEmailModalVisible: false,
      puppyReleaseId: null as number | null,
      emailToSend: "",
      firstNameToSend: "",
      lastNameToSend: "",
      searchParams: {
        litterId: "",
        dogId: "",
        releasePersonEmail: "",
        releasePersonName: "",
        releaseDateFrom: "",
        releaseDateTo: "",
        registrationStatus: null as any,
      },
      options: {} as PaginationOptions,
      headers: [
        { text: "", align: "left", value: "litterId", width: '8%' },
        { text: "Body color & gender", align: "left", value: "puppyInfo", width: '7%' },
        { text: "Dog ID", align: "left", value: "dogId", width: '7%' },
        { text: "", align: "left", value: "releaseDate", width: '15%' },
        { text: "", align: "left", value: "releasedPerson", width: '18%' },
        { text: "Signature Status", align: "left", value: "signatureStatus", width: '25%' },
        { text: "", align: "left", value: "registrationStatus", width: '10%' },
        { text: "", align: "left", value: "actionButton", width: '10%' },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
      currentCheckboxIdx: 0,
    };
  },
  created: function() {
    this.load();
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    clearAllFilters: function () {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.searchParams = {
        litterId: "",
        dogId: "",
        releasePersonEmail: "",
        releasePersonName: "",
        releaseDateFrom: "",
        releaseDateTo: "",
        registrationStatus: null,
      };
    },
    toggleCheckbox: function() {
      var states = [null, true, false];
      var next = (++this.currentCheckboxIdx) % states.length;
      this.currentCheckboxIdx = next;
      this.searchParams.registrationStatus = states[next];
    },
    updateDates: function (val: any, key: string) {
      (this as any).searchParams[key] = val.date;
    },
    updateDateSearch: async function(val: any, key: string) {
      await this.updateDates(val, key);
      this.applyFilter();
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        const response = await dogService.getPuppyReleaseList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        const errors = (ex?.response ?? ex)?.data?.errors;
        const descriptiveMessage = errors
          ? `Error(s): ${Object.values(errors).join(" & ")}`
          : null

        this.errorMessage = descriptiveMessage ?? ex?.message ?? "Unknown error occurred when retrieving data.";
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    },
    sendEmail() {
      if (this.puppyReleaseId == null) return;
      const showFailure = () => {
        this.isError = true;
        this.errorMessage = "We couldn't complete that action. Please try again.";
        this.hideEmailModal();
      };

      dogService.resendReleaseEmail({
        puppyReleaseId: this.puppyReleaseId,
        email: this.emailToSend,
        firstName: this.firstNameToSend,
        lastName: this.lastNameToSend,
      }).then(r => {
        if (r.errorMessage) {
          Sentry.withScope(scope => {
            scope.setContext("puppy release", {
              puppyReleaseId: this.puppyReleaseId,
              errorMessage: r.errorMessage,
            });
            Sentry.captureMessage("Puppy release email resend failed");
          });
          showFailure();
          return;
        }
        if (!r.success) {
          Sentry.withScope(scope => {
            scope.setContext("puppy release", { puppyReleaseId: this.puppyReleaseId });
            Sentry.captureMessage("Puppy release email resend returned no error details");
          });
          showFailure();
          return;
        }
        this.hideEmailModal();
      }).catch((e: any) => {
        const status = (e?.response ?? e)?.status;
        if (status && status < 500) {
          Sentry.withScope(scope => {
            scope.setContext("puppy release", { puppyReleaseId: this.puppyReleaseId, status });
            Sentry.captureException(e);
          });
        }
        showFailure();
      })

    },
    showEmailModal(email: string, first: string, last: string, puppyReleaseId: number){
      this.isEmailModalVisible = true;
      this.emailToSend = email ?? "";
      this.firstNameToSend = first ?? "";
      this.lastNameToSend = last ?? "";
      this.puppyReleaseId = puppyReleaseId;
    },
    hideEmailModal(){
      this.isEmailModalVisible = false;
      this.emailToSend = "";
      this.firstNameToSend = "";
      this.lastNameToSend = "";
      this.puppyReleaseId = null;
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
  },
});
</script>
